import React, {RefObject} from 'react';
import findIndex from 'lodash/findIndex';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {Modal, DisplayText, TextContainer, Heading} from '@shopify/polaris';
import {WorkSample} from 'sections/types';

import * as styles from './WorkSamplesContainer.scss';

import i18nArgs from '../../locales';

interface OwnProps {
  workSamples: WorkSample[];
}

type CombinedProps = OwnProps & WithI18nProps;

interface State {
  modalOpen: boolean;
  activeWorkSample?: WorkSample;
}

class WorkSampleForm extends React.Component<CombinedProps, State> {
  state: State = {
    modalOpen: false,
    activeWorkSample: undefined,
  };

  scrollRef: RefObject<HTMLDivElement> = React.createRef();

  openModal = ({workSample}: {workSample: WorkSample}) => {
    return () => {
      this.setState(({modalOpen}) => ({
        modalOpen: !modalOpen,
        activeWorkSample: workSample,
      }));
    };
  };

  closeModal = () => {
    this.setState({
      modalOpen: false,
    });
  };

  switchActiveWorkSample({direction}: {direction: string}) {
    const {workSamples} = this.props;
    const {activeWorkSample} = this.state;

    return () => {
      const currentIndex = findIndex(workSamples, activeWorkSample);
      const nextIndex =
        direction === 'next' ? currentIndex + 1 : currentIndex - 1;

      this.setState(
        {
          activeWorkSample: workSamples[nextIndex],
        },
        this.scrollToRef
      );
    };
  }

  scrollToRef = () => {
    if (!this.scrollRef.current) return;
    this.scrollRef.current.scrollIntoView();
  };

  renderModal() {
    const {workSamples, i18n} = this.props;
    const {activeWorkSample, modalOpen} = this.state;
    const currentIndex = findIndex(workSamples, activeWorkSample);
    const hasNext = currentIndex < workSamples.length - 1;
    const hasPrev = currentIndex > 0;

    return (
      <Modal
        large
        title={activeWorkSample ? activeWorkSample.title : null}
        open={modalOpen}
        onClose={this.closeModal}
        primaryAction={{
          content: 'Next',
          onAction: this.switchActiveWorkSample({direction: 'next'}),
          disabled: !hasNext,
        }}
        secondaryActions={[
          {
            content: 'Previous',
            onAction: this.switchActiveWorkSample({direction: 'prev'}),
            disabled: !hasPrev,
          },
        ]}
      >
        <Modal.Section>
          {activeWorkSample && (
            <div className={styles.Modal}>
              <div className={styles.ModalContent}>
                <div className={styles.Sticky}>
                  <TextContainer spacing="tight">
                    <Heading>
                      {i18n.translate(
                        'Profile.WorkSample.workSample.descriptionTitle'
                      )}
                    </Heading>
                    <p>{activeWorkSample.description}</p>
                  </TextContainer>
                </div>
              </div>
              <div className={styles.ModalImages} ref={this.scrollRef}>
                {activeWorkSample.images.map(image => (
                  <img
                    src={getSrcFromSrcSet(image.srcSet)}
                    srcSet={image.srcSet.join(',')}
                    sizes="
                      (min-width: 900px) 600px,
                      (min-width: 768px) 400px,
                      100vw
                    "
                    key={image.id}
                    alt=""
                  />
                ))}
              </div>
            </div>
          )}
        </Modal.Section>
      </Modal>
    );
  }

  renderThumbnails() {
    const {workSamples, i18n} = this.props;

    return (
      <>
        <DisplayText element="h2" size="medium">
          {i18n.translate('Profile.WorkSample.title')}
        </DisplayText>
        <div className={styles.Thumbnails}>
          {workSamples.map(workSample => {
            if (!workSample.images.length) return null;

            const coverImage = workSample.images[0];

            return (
              <div key={workSample.id}>
                <div className={styles.ThumbnailContainer}>
                  <div className={styles.ThumbnailImageContainer}>
                    <img
                      className={styles.ThumbnailImage}
                      src={getSrcFromSrcSet(coverImage.srcSet)}
                      srcSet={coverImage.srcSet.join(',')}
                      sizes="
                      (min-width: 900px) 300px,
                      (min-width: 768px) 400px,
                      (min-width: 565px) 50vw,
                      100vw
                    "
                      alt={workSample.title}
                    />
                  </div>
                  <button
                    className={styles.ThumbnailButton}
                    onClick={this.openModal({workSample})}
                    type="button"
                  >
                    <span>{workSample.title}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  render() {
    const {workSamples} = this.props;

    return workSamples && workSamples.length > 0 ? (
      <div className={styles.WorkSamples}>
        {this.renderThumbnails()}
        {this.renderModal()}
      </div>
    ) : null;
  }
}

function getSrcFromSrcSet(srcSet: string[]) {
  return srcSet[Math.floor(srcSet.length / 2)];
}

export default withI18n(i18nArgs)(WorkSampleForm);
